import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { SupportEmailLink } from "components/app/support-email-link";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContentHeader, Stage } from "components/layout";
import { InlineBanner } from "components/ui/inline-banner";
import { useGetRecentConversationFeedForUserQuery } from "generated/graphql";
import React from "react";
import { Link, useParams } from "react-router-dom";
import { useFeature, useRegionId } from "store/selectors/hooks";
import { usePhoneLines } from "store/selectors/telecom";
import { ConversationFeed } from "./conversation-feed";
import { useUserMailboxes } from "./use-user-mailboxes";

const MessagingSetupChecklist: React.FC<{
    phoneLinesConfigured: boolean;
    mailboxSyncEnabled: boolean;
    hasActiveMailbox: boolean;
}> = ({ phoneLinesConfigured, mailboxSyncEnabled, hasActiveMailbox }) => {
    return (
        <InlineBanner type="info" showIcon={false}>
            <p className="font-medium mb-3">
                Get started with messaging by setting up your communication channels:
            </p>
            <ul className="space-y-2">
                <li className="flex items-start gap-2">
                    <CheckCircleIcon
                        className={`size-5 mt-0.5 shrink-0 ${phoneLinesConfigured ? "text-green-500" : "text-blue-300"}`}
                    />
                    <Link
                        to="/settings/account/phone"
                        className={
                            phoneLinesConfigured
                                ? "text-blue-400 line-through"
                                : "text-blue-800 underline"
                        }>
                        Set up a phone line to send and receive text messages
                    </Link>
                </li>
                <li className="flex items-start gap-2">
                    {mailboxSyncEnabled ? (
                        <>
                            <CheckCircleIcon
                                className={`size-5 mt-0.5 shrink-0 ${hasActiveMailbox ? "text-green-500" : "text-blue-300"}`}
                            />
                            <Link
                                to="/settings/account/email"
                                className={
                                    hasActiveMailbox
                                        ? "text-blue-400 line-through"
                                        : "text-blue-800 underline"
                                }>
                                Connect your email to sync your inbox
                            </Link>
                        </>
                    ) : (
                        <>
                            <CheckCircleIcon className="size-5 mt-0.5 shrink-0 text-blue-300" />
                            <span>
                                We&#39;re rolling out Mailbox Sync! Reach out to{" "}
                                <SupportEmailLink
                                    label="Support"
                                    subject="Mailbox Sync Early Access"
                                />{" "}
                                if you&#39;d like to participate in the early release.
                            </span>
                        </>
                    )}
                </li>
            </ul>
            <p className="mt-3">
                Contact <SupportEmailLink label="support" subject="Messaging Setup" /> if
                you need help getting started.
            </p>
        </InlineBanner>
    );
};

export const MessagingDashboard: React.FC = () => {
    const { threadId } = useParams<{ threadId: string }>();
    const mailboxSyncEnabled = useFeature("mailboxSync");
    const { hasActiveMailbox } = useUserMailboxes();
    const phoneLines = usePhoneLines();

    const phoneLinesConfigured = phoneLines.length > 0;
    const hasAnyChannel = hasActiveMailbox || phoneLinesConfigured;

    const showChecklist =
        !phoneLinesConfigured || !hasActiveMailbox || !mailboxSyncEnabled;

    if (!hasAnyChannel) {
        return (
            <Stage>
                <div className="flex flex-col gap-4 p-4">
                    <ContentHeader>Messages</ContentHeader>
                    <MessagingSetupChecklist
                        phoneLinesConfigured={false}
                        mailboxSyncEnabled={mailboxSyncEnabled}
                        hasActiveMailbox={false}
                    />
                </div>
            </Stage>
        );
    }

    return (
        <Stage>
            <div className="flex flex-col gap-4 p-4">
                <ContentHeader>Latest conversations</ContentHeader>
                {showChecklist ? (
                    <MessagingSetupChecklist
                        phoneLinesConfigured={phoneLinesConfigured}
                        mailboxSyncEnabled={mailboxSyncEnabled}
                        hasActiveMailbox={hasActiveMailbox}
                    />
                ) : null}
                <RecentConversationsFeed initialThreadId={threadId} />
            </div>
        </Stage>
    );
};

interface RecentConversationsFeedProps {
    initialThreadId: string | null;
}

const RecentConversationsFeed: React.FC<RecentConversationsFeedProps> = props => {
    const { initialThreadId } = props;
    const regionId = useRegionId();

    const query = useGetRecentConversationFeedForUserQuery({ regionId });

    return (
        <QueryRenderer
            query={query}
            name="RecentConversationsFeed.getRecentConversationFeedForUser">
            {data => {
                return (
                    <>
                        <ConversationFeed
                            feed={data?.getRecentConversationFeedForUser}
                            initialThreadId={initialThreadId}
                        />
                    </>
                );
            }}
        </QueryRenderer>
    );
};
